"use client";

import { useState } from "react";
import { ArrowLeft, Star, Gift, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CommingSoon from "../../components/CommingSoon";

const MyCollectibles = () => {
  const router = useNavigate();
  const [selectedTab, setSelectedTab] = useState("collection");
  const [clickedItem, setClickedItem] = useState(null);

  const handleItemClick = (id) => {
    setClickedItem(id);
    setTimeout(() => setClickedItem(null), 400);
  };

  const collectedItems = [
    {
      id: 1,
      name: "Whiskers",
      description: "A playful kitten that purrs when you learn new words",
      emoji: "🐱",
      category: "Pets",
      rarity: "Common",
      rarityColor: "bg-gray-400",
      bgColor: "from-orange-200 to-yellow-200",
    },
    {
      id: 2,
      name: "Magic Pencil",
      description: "A magical pencil that helps you draw better",
      emoji: "✏️",
      category: "Toys",
      rarity: "Common",
      rarityColor: "bg-gray-400",
      bgColor: "from-blue-200 to-purple-200",
    },
    {
      id: 3,
      name: "Golden Star",
      description: "A shining star earned for perfect spelling",
      emoji: "⭐",
      category: "Achievements",
      rarity: "Rare",
      rarityColor: "bg-blue-500",
      bgColor: "from-yellow-200 to-orange-200",
    },
    {
      id: 4,
      name: "Rainbow Dragon",
      description: "A magical dragon that breathes colorful words",
      emoji: "🐉",
      category: "Pets",
      rarity: "Epic",
      rarityColor: "bg-purple-500",
      bgColor: "from-purple-200 to-pink-200",
    },
  ];

  //   const availableItems = [
  //     {
  //       id: 5,
  //       name: "Crystal Owl",
  //       description: "Unlock by completing 50 lessons",
  //       emoji: "🦉",
  //       category: "Pets",
  //       rarity: "Rare",
  //       rarityColor: "bg-blue-500",
  //       requirement: "Complete 50 lessons",
  //       progress: 35,
  //       bgColor: "from-gray-200 to-gray-300",
  //     },
  //     {
  //       id: 6,
  //       name: "Magic Wand",
  //       description: "Unlock by scoring 100% on 10 tests",
  //       emoji: "🪄",
  //       category: "Toys",
  //       rarity: "Epic",
  //       rarityColor: "bg-purple-500",
  //       requirement: "Score 100% on 10 tests",
  //       progress: 6,
  //       bgColor: "from-gray-200 to-gray-300",
  //     },
  //     {
  //       id: 7,
  //       name: "Phoenix",
  //       description: "Unlock by maintaining a 30-day streak",
  //       emoji: "🔥",
  //       category: "Pets",
  //       rarity: "Legendary",
  //       rarityColor: "bg-red-500",
  //       requirement: "30-day learning streak",
  //       progress: 18,
  //       bgColor: "from-gray-200 to-gray-300",
  //     },
  //   ];

  const renderCollectibleCard = (item, isLocked = false) => (
    <div
      key={item.id}
      onClick={() => handleItemClick(item.id)}
      className={`group relative bg-gradient-to-br ${
        item.bgColor
      } rounded-3xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer border-4 border-white/40 ${
        clickedItem === item.id ? "animate-pulse scale-95" : ""
      } ${isLocked ? "opacity-75" : ""}`}
    >
      {isLocked && (
        <div className="absolute top-4 right-4 bg-gray-500 text-white rounded-full p-2">
          <Lock className="w-4 h-4" />
        </div>
      )}

      <div className="text-center">
        <div
          className={`text-6xl mb-4 group-hover:scale-110 transition-transform duration-300 ${
            isLocked ? "grayscale" : ""
          }`}
        >
          {item.emoji}
        </div>

        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-purple-600 transition-colors">
          {item.name}
        </h3>

        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          {item.description}
        </p>

        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-gray-500 bg-white/60 px-3 py-1 rounded-full">
            {item.category}
          </span>
          <span
            className={`text-xs font-bold text-white px-3 py-1 rounded-full ${item.rarityColor}`}
          >
            {item.rarity}
          </span>
        </div>

        {isLocked ? (
          <div className="space-y-2">
            <div className="text-xs text-gray-600 font-medium">
              {item.requirement}
            </div>
            <div className="w-full bg-gray-300 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-400 to-pink-400 h-2 rounded-full transition-all duration-500"
                style={{
                  width: `${
                    (item.progress /
                      (item.requirement.includes("50")
                        ? 50
                        : item.requirement.includes("10")
                        ? 10
                        : 30)) *
                    100
                  }%`,
                }}
              ></div>
            </div>
            <div className="text-xs text-gray-500">
              {item.progress}/
              {item.requirement.includes("50")
                ? 50
                : item.requirement.includes("10")
                ? 10
                : 30}
            </div>
          </div>
        ) : (
          <div className="bg-green-400 text-white text-sm font-bold py-2 px-4 rounded-full group-hover:bg-green-500 transition-colors">
            Collected! ✓
          </div>
        )}
      </div>

      {/* Decorative elements */}
      <div className="absolute -right-4 -top-4 w-16 h-16 bg-white/20 rounded-full group-hover:scale-125 transition-transform duration-500"></div>
      <div className="absolute -left-2 -bottom-2 w-10 h-10 bg-white/15 rounded-full group-hover:scale-110 transition-transform duration-700"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-200 via-purple-200 to-indigo-200 p-6">
      <CommingSoon className="fixed top-6 right-10 z-50" />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/30">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => router(-1)}
                className="p-3 bg-gradient-to-r from-purple-400 to-pink-400 text-white rounded-full hover:scale-110 transition-transform duration-200"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  My Collectibles
                </h1>
                <p className="text-gray-600">
                  Hello, Aarav! You have {collectedItems.length} items
                </p>
              </div>
              <div className="ml-auto text-4xl animate-bounce">🎁</div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-4">
              <button
                onClick={() => setSelectedTab("collection")}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                  selectedTab === "collection"
                    ? "bg-gradient-to-r from-purple-400 to-pink-400 text-white shadow-lg transform scale-105"
                    : "bg-white/60 text-gray-600 hover:bg-white/80"
                }`}
              >
                <Gift className="w-5 h-5" />
                My Collection
              </button>
              {/* <button
                onClick={() => setSelectedTab("available")}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                  selectedTab === "available"
                    ? "bg-gradient-to-r from-blue-400 to-cyan-400 text-white shadow-lg transform scale-105"
                    : "bg-white/60 text-gray-600 hover:bg-white/80"
                }`}
              >
                <Sparkles className="w-5 h-5" />
                Available
              </button> */}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {selectedTab === "collection" && (
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <Star className="w-8 h-8 text-yellow-500" />
                Your Amazing Collection
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {collectedItems.map((item) => renderCollectibleCard(item))}
              </div>
            </div>
          )}

          {/* {selectedTab === "available" && (
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <Lock className="w-8 h-8 text-blue-500" />
                Coming Soon
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableItems.map((item) => renderCollectibleCard(item, true))}
              </div>
            </div>
          )} */}
        </div>

        {/* Floating Decorative Elements */}
        <div
          className="fixed top-16 right-8 text-4xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "0.5s" }}
        >
          🎈
        </div>
        <div
          className="fixed bottom-32 left-8 text-3xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "1.2s" }}
        >
          🌟
        </div>
        <div
          className="fixed top-1/3 left-4 text-2xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "2s" }}
        >
          ✨
        </div>
        <div
          className="fixed top-2/3 right-6 text-3xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "0.8s" }}
        >
          🎉
        </div>
        <div
          className="fixed bottom-1/4 right-20 text-2xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "1.5s" }}
        >
          🦄
        </div>
      </div>
    </div>
  );
};

export default MyCollectibles;
